import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface Rate {
  currency: string;
  rate: number;
  rate_date: string;
  first_date: string | null;
}

interface RatesResponse {
  base_currency: string;
  date: string;
  requested_date: string;
  date_range: { from: string; to: string };
  count: number;
  rates: Rate[];
}

const CURRENCY_NAMES: Record<string, string> = {
  AUD: 'Australischer Dollar', BRL: 'Brasilianischer Real', CAD: 'Kanadischer Dollar',
  CHF: 'Schweizer Franken', CNY: 'Chinesischer Yuan', CZK: 'Tschechische Krone',
  DKK: 'Dänische Krone', GBP: 'Britisches Pfund', HKD: 'Hongkong-Dollar',
  HUF: 'Ungarischer Forint', IDR: 'Indonesische Rupiah', ILS: 'Israelischer Schekel',
  INR: 'Indische Rupie', ISK: 'Isländische Krone', JPY: 'Japanischer Yen',
  KRW: 'Südkoreanischer Won', MXN: 'Mexikanischer Peso', MYR: 'Malaysischer Ringgit',
  NOK: 'Norwegische Krone', NZD: 'Neuseeland-Dollar', PHP: 'Philippinischer Peso',
  PLN: 'Polnischer Zloty', RON: 'Rumänischer Leu', SEK: 'Schwedische Krone',
  SGD: 'Singapur-Dollar', THB: 'Thailändischer Baht', TRY: 'Türkische Lira',
  USD: 'US-Dollar', ZAR: 'Südafrikanischer Rand',
};

const CURRENCY_FLAGS: Record<string, string> = {
  AUD: '🇦🇺', BRL: '🇧🇷', CAD: '🇨🇦', CHF: '🇨🇭', CNY: '🇨🇳', CZK: '🇨🇿',
  DKK: '🇩🇰', GBP: '🇬🇧', HKD: '🇭🇰', HUF: '🇭🇺', IDR: '🇮🇩', ILS: '🇮🇱',
  INR: '🇮🇳', ISK: '🇮🇸', JPY: '🇯🇵', KRW: '🇰🇷', MXN: '🇲🇽', MYR: '🇲🇾',
  NOK: '🇳🇴', NZD: '🇳🇿', PHP: '🇵🇭', PLN: '🇵🇱', RON: '🇷🇴', SEK: '🇸🇪',
  SGD: '🇸🇬', THB: '🇹🇭', TRY: '🇹🇷', USD: '🇺🇸', ZAR: '🇿🇦',
};

const COL: React.CSSProperties = { padding: '10px 12px', fontSize: '0.75rem', whiteSpace: 'nowrap', textAlign: 'left' };
const HDR: React.CSSProperties = { ...COL, color: '#4A5068', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #1E2330' };

export default function RatesPage() {
  const [data, setData] = useState<RatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');

  const fetchRates = (date?: string) => {
    setLoading(true);
    const params = date ? `?date=${date}` : '';
    fetch(`${API_BASE}/market/exchange-rates${params}`)
      .then(res => res.json())
      .then(d => {
        setData(d);
        setSelectedDate(d.date);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRates(); }, []);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setSelectedDate(date);
    fetchRates(date);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '4px' }}>
            <i className="fa-solid fa-euro-sign" style={{ marginRight: '10px', color: '#00D97E' }}></i>Wechselkurse
          </h1>
          <p style={{ color: '#7B8399', fontSize: '0.8rem' }}>
            EZB Referenzkurse · Basis: EUR
            {data && ` · ${data.count} Währungen`}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {data && data.date !== data.requested_date && (
            <span style={{ fontSize: '0.7rem', color: '#FBBF24' }}>
              Keine Daten für {data.requested_date} — zeige {data.date}
            </span>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '0.6rem', fontWeight: 600, color: '#4A5068', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Stichtag
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              min={data?.date_range.from}
              max={data?.date_range.to}
              style={{
                padding: '8px 12px',
                backgroundColor: '#1E2330',
                border: '1px solid #2A3040',
                borderRadius: '6px',
                color: '#E8EAED',
                fontSize: '0.8rem',
                outline: 'none',
                colorScheme: 'dark',
              }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#4A5068', padding: '40px', textAlign: 'center' }}>Lade Wechselkurse...</div>
      ) : data ? (
        <div style={{
          backgroundColor: '#151921', border: '1px solid #1E2330',
          borderRadius: '12px', overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={HDR}>Währung</th>
                <th style={HDR}>Name</th>
                <th style={{ ...HDR, textAlign: 'right' }}>1 EUR =</th>
                <th style={{ ...HDR, textAlign: 'right' }}>1 {'{X}'} = EUR</th>
                <th style={HDR}>Kursdatum</th>
                <th style={HDR}>Daten seit</th>
              </tr>
            </thead>
            <tbody>
              {data.rates.map(rate => (
                <tr
                  key={rate.currency}
                  style={{ borderBottom: '1px solid #1E2330' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1A1F2B'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={COL}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '1rem' }}>{CURRENCY_FLAGS[rate.currency] || '🏳️'}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700,
                        backgroundColor: '#1E2330', color: '#E8EAED', fontFamily: 'monospace',
                      }}>
                        {rate.currency}
                      </span>
                    </span>
                  </td>
                  <td style={{ ...COL, color: '#7B8399' }}>
                    {CURRENCY_NAMES[rate.currency] || rate.currency}
                  </td>
                  <td style={{ ...COL, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.85rem' }}>
                    {rate.rate < 10
                      ? rate.rate.toFixed(6)
                      : rate.rate < 1000
                        ? rate.rate.toFixed(4)
                        : rate.rate.toFixed(2)}
                  </td>
                  <td style={{ ...COL, textAlign: 'right', fontFamily: 'monospace', color: '#7B8399', fontSize: '0.8rem' }}>
                    {(1 / rate.rate).toFixed(6)}
                  </td>
                  <td style={{ ...COL, color: '#7B8399', fontSize: '0.7rem' }}>
                    {rate.rate_date}
                  </td>
                  <td style={{ ...COL, color: '#4A5068', fontSize: '0.7rem' }}>
                    {rate.first_date || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
